import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { encrypttoHEX } from "./encodedecode.js";
import MediaComponent from "../components/MediaComponent.js";
import PollComponent from "../components/PollComponent.js";
import TextComponent from "../components/TextComponent.js";
import SpeechAssessmentComponent from "../components/SpeechAssessmentComponent.js";
import SubmitFeedComponent from "../components/SubmitFeedComponent.js";
import LeftTab from "../components/feedconstruction/LeftTab.js";
import RightTab from "../components/feedconstruction/RightTab.js";

const DNDKitSortableList = ({ id, children, handleRemoveAsset }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;

  return (
    <div className="space-y-2" ref={setNodeRef} style={style}>
      <div className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div
          className="flex flex-1 items-center"
          {...attributes}
          {...listeners}
        >
          <div className="ml-2 flex-1">
            <p className="font-medium">{children.AssetName}</p>
            <p className="truncate text-sm text-gray-500">{children.Caption}</p>
            <p className="text-xs text-gray-400">{children.BrandName}</p>
          </div>
        </div>
        <button
          onClick={() => handleRemoveAsset(children.AssetID)}
          className="ml-4 rounded-md px-3 py-1 text-sm text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

const NewFeedConstruction = ({ token }) => {
  const location = useLocation();
  const { existingFeed, preSelectedAssets } = location.state || {};

  const [formData, setFormData] = useState({
    Name:
      existingFeed?.Name || `Feed_${new Date().toISOString().split("T")[0]}`,
    SessionLink: existingFeed?.SessionLink || "",
    SessionStartTime:
      existingFeed?.SessionStartTime || new Date().toISOString().slice(0, 16),
    SessionEndTime:
      existingFeed?.SessionEndTime ||
      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    MaxStarted: existingFeed?.MaxStarted || "10000",
    MaxCompleted: existingFeed?.MaxCompleted || "10000",
    FeedType: existingFeed?.FeedType || "360",
  });

  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState(preSelectedAssets || []);
  const [filter, setFilter] = useState("all");
  const [brandNames, setBrandNames] = useState([]);
  const [filterBrandName, setFilterBrandName] = useState("all");
  const [filterAsset, setFilterAsset] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchCategory, setSearchCategory] = useState("caption");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("assets");

  const [feedCreationSuccess, setFeedCreationSuccess] = useState(false);
  const [feedURL, setFeedURL] = useState("");
  const [brandReportURLs, setBrandReportURLs] = useState([]);
  const [createdFeedID, setCreatedFeedID] = useState("");

  const [feedType, setFeedType] = useState("360");

  const [posts, setPosts] = useState([]);

  const baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ""}/`;

  useEffect(() => {
    setIsLoading(true);
    axios
      .get("https://genz-staging.feeltiptop.com/api/asset/getAssets", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setAssets(response.data);
        const uniqueBrands = [
          ...new Set(response.data.map((asset) => asset.BrandName)),
        ].filter(Boolean);
        setBrandNames(uniqueBrands);
        setFilterAsset(response.data);
      })
      .catch((error) => {
        console.error("Error fetching assets:", error);
        setError("Failed to load assets. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token]);

  useEffect(() => {
    let filtered = assets;

    if (filterBrandName !== "all") {
      filtered = filtered.filter(
        (asset) => asset.BrandName === filterBrandName,
      );
    }

    if (filter !== "all") {
      filtered = filtered.filter((asset) => {
        try {
          const content = JSON.parse(asset.AssetContent);
          if (filter === "image") {
            return (
              content.assetType === "media" &&
              content.mediaContent.match(/\.(jpg|jpeg|png|gif)$/i)
            );
          }
          if (filter === "video") {
            return (
              content.assetType === "media" &&
              content.mediaContent.match(/\.(mp4|webm|mpeg)$/i)
            );
          }
          return content.assetType === filter;
        } catch (error) {
          console.error("Error parsing asset content:", error);
          return false;
        }
      });
    }

    if (searchInput) {
      const searchField =
        searchCategory === "caption" ? "Caption" : "AssetName";
      filtered = filtered.filter((asset) =>
        asset[searchField]?.toLowerCase().includes(searchInput.toLowerCase()),
      );
    }

    console.log("Filtered assets:", filtered); // Debugging line

    setFilterAsset(filtered);
  }, [assets, filter, filterBrandName, searchInput, searchCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAsset = (asset) => {
    console.log("Adding in asset: ", asset, "selectedAssets: ", selectedAssets);
    if (selectedAssets.some((selected) => selected.AssetID === asset.AssetID)) {
      alert("This asset is already selected.");
      return;
    }
    setSelectedAssets((prev) => [...prev, asset]);
  };

  const handleRemoveAsset = (assetID) => {
    setSelectedAssets((prev) =>
      prev.filter((asset) => asset.AssetID !== assetID),
    );
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setSelectedAssets((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const createObfuscatedReportURL = (brandID, feedID) => {
    const encryptSTR = JSON.stringify({ feedID: feedID, brandID: brandID });
    return "Report?p=" + encrypttoHEX(encryptSTR);
  };

  const createObfuscatedFeedURL = (feedID) => {
    const encryptSTR = JSON.stringify({ feedID: feedID });
    return "Login?p=" + encrypttoHEX(encryptSTR);
  };

  const handleFinalize = async () => {
    if (!selectedAssets.length) {
      setError("Please select at least one asset for the feed.");
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date();
      const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
      const formatDateTime = (date) =>
        date.toISOString().slice(0, 19).replace("T", " ");

      const submissionData = {
        ...formData,
        SessionStartTime: formData.SessionStartTime
          ? formatDateTime(new Date(formData.SessionStartTime))
          : formatDateTime(now),
        SessionEndTime: formData.SessionEndTime
          ? formatDateTime(new Date(formData.SessionEndTime))
          : formatDateTime(oneHourLater),
      };

      // Create feed first
      const createResponse = await axios.post(
        "https://genz-staging.feeltiptop.com/api/feed/create",
        submissionData,
        {
          headers: { Authorization: "Bearer " + token },
        },
      );

      if (createResponse.data.success) {
        const feedId = createResponse.data.feedId;
        setCreatedFeedID(feedId);

        let assets = [];

        selectedAssets.map((element, index) => {
          assets.push({
            assetId: element.AssetID,
            position: index,
          });
        });

        console.log("current assets: ", assets);

        // Add assets
        await axios.post(
          `https://genz-staging.feeltiptop.com/api/feed/${feedId}/assets/add`,
          {
            assets: assets,
          },
          {
            headers: { Authorization: "Bearer " + token },
          },
        );

        // Generate feed URL
        const newFeedURL = baseURL + createObfuscatedFeedURL(feedId);
        setFeedURL(newFeedURL);

        // Add the feed URL
        await axios.post(
          "https://genz-staging.feeltiptop.com/api/addFeedURL",
          {
            FeedID: feedId,
            SessionLink: newFeedURL,
          },
          {
            headers: { Authorization: "Bearer " + token },
          },
        );

        // Generate brand report URLs
        const uniqueBrands = [
          ...new Set(
            selectedAssets.map((asset) => ({
              brandID: asset.BrandID,
              brandName: asset.BrandName,
            })),
          ),
        ];

        const reportURLs = uniqueBrands.map((brand) => ({
          id: brand.brandID,
          name: brand.brandName,
          reporturl: createObfuscatedReportURL(brand.brandID, feedId),
        }));
        setBrandReportURLs(reportURLs);

        setFeedCreationSuccess(true);
      }
    } catch (error) {
      console.error("Error creating feed:", error);
      setError(
        error.response?.data?.message ||
          "Failed to create feed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id === over.id) {
      return;
    }
    const oldIndex = selectedAssets.findIndex(
      (asset) => asset.AssetID === active.id,
    );
    const newIndex = selectedAssets.findIndex(
      (asset) => asset.AssetID === over.id,
    );

    if (oldIndex !== newIndex) {
      const updatedAssets = arrayMove(selectedAssets, oldIndex, newIndex);
      console.log("Updated assets:", updatedAssets);

      setSelectedAssets(updatedAssets);
    }
  };

  // Asset Content Renderer
  const renderAssetContent = (asset) => {
    try {
      const content = JSON.parse(asset.AssetContent);
      console.log("Asset content:", content);
      switch (content.assetType) {
        case "poll":
          if (content.pollType === "survey") {
            return (
              <FeedPollSurvey
                surveyPolls={content.selections}
                assetContent={content}
                userID={() => {}}
                feedID={createdFeedID}
                activeAssetId={asset.AssetID}
                token={token}
                setToken={() => {}} // Placeholder since we're in preview mode
              />
            );
          } else if (content.pollType === "submit_button") {
            return (
              <SubmitFeedComponent
                assetContent={content}
                activeAssetId={asset.AssetID}
                feedID={createdFeedID}
                token={token}
                setToken={() => {}} // Placeholder since we're in preview mode
              />
            );
          }
          return (
            <div className="rounded-lg bg-gray-50 p-4">
              <PollComponent
                assetContent={content}
                token={token}
                activeAssetId={asset.AssetID}
                feedID={createdFeedID}
                setToken={() => {}} // Placeholder since we're in preview mode
              />
            </div>
          );
        case "text":
          return (
            <div className="prose prose-sm max-w-none">
              <TextComponent assetContent={content} />
            </div>
          );
        case "media":
          return (
            <div className="relative aspect-video">
              <MediaComponent mediaLink={content.mediaContent} />
            </div>
          );
        case "SpeechAsset":
          return (
            <div className="relative aspect-video">
              <SpeechAssessmentComponent
                mediaLink={content.mediaContent}
                activeAssetId={asset.AssetID}
                feedID={createdFeedID}
                caption={asset.Caption}
                token={token}
              />
            </div>
          );
        default:
          return <p>Unsupported asset type: {content.assetType}</p>;
      }
    } catch (error) {
      console.error("Error rendering asset content:", error);
      return <p>Error displaying content</p>;
    }
  };

  // Success Modal Component
  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-green-600">
            Feed Created Successfully!
          </h2>
          <button
            onClick={() => setFeedCreationSuccess(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="mb-2 font-semibold">Feed URL:</h3>
            <a
              href={feedURL}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-blue-600 hover:underline"
            >
              {feedURL}
            </a>
          </div>

          {/* <div>
            <h3 className="mb-2 font-semibold">Brand Report URLs:</h3>
            <ul className="space-y-2">
              {brandReportURLs.map((brand) => (
                <li key={brand.id}>
                  <span className="font-medium">{brand.name}</span>
                  <a
                    href={`${baseURL}${brand.reporturl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 break-all text-blue-600 hover:underline"
                  >
                    {`${baseURL}${brand.reporturl}`}
                  </a>
                </li>
              ))}
            </ul>
          </div> */}

          {/* <div>
            <h3 className="mb-2 font-semibold">Feed Data Export:</h3>
            <a
              href={`${baseURL}FeedSessionExport?feedid=${createdFeedID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-blue-600 hover:underline"
            >
              {`${baseURL}FeedSessionExport?feedid=${createdFeedID}`}
            </a>
          </div> */}

          {/* <div>
            <h3 className="mb-2 font-semibold">Ambassador Profiles:</h3>
            <a
              href={`${baseURL}AmbassadorProfiles`}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-blue-600 hover:underline"
            >
              {`${baseURL}AmbassadorProfiles`}
            </a>
          </div> */}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* {filterAsset.map((asset, index) => (
        <div key={index} className="">
          <h1>Assety Title: {asset.AssetName}</h1>
        </div>
      ))} */}
      <LeftTab
        handleInputChange={handleInputChange}
        formData={formData}
        existingFeed={existingFeed}
        handleAddAsset={handleAddAsset}
        filterAsset={filterAsset}
        isLoading={isLoading}
        error={error}
        brandNames={brandNames}
        setSearchCategory={setSearchCategory}
        searchCategory={searchCategory}
        setSearchInput={setSearchInput}
        searchInput={searchInput}
        setFilterBrandName={setFilterBrandName}
        filterBrandName={filterBrandName}
        setFilter={setFilter}
        setFeedType={setFeedType}
        filter={filter}
        posts={posts}
      />

      <RightTab
        selectedAssets={selectedAssets}
        setSelectedAssets={setSelectedAssets}
        filter={filter}
        setFilter={setFilter}
        handleFinalize={handleFinalize}
        isLoading={isLoading}
        error={error}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setError={setError}
      />
      {feedCreationSuccess && (
        <SuccessModal
          setFeedCreationSuccess={setFeedCreationSuccess}
          feedURL={feedURL}
          brandReportURLs={brandReportURLs}
          createdFeedID={createdFeedID}
          baseURL={baseURL}
        />
      )}

      {error && (
        <div className="fixed bottom-4 right-4 rounded-lg border border-red-400 bg-red-100 px-4 py-3 text-red-700 shadow-lg">
          <p className="flex items-center">
            <span className="mr-2">⚠️</span>
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default NewFeedConstruction;
